/**
 * Cloud-mode auth state. Lives at the top of the cloud app tree.
 *
 * Status flow:
 *   loading → (call /v1/auth/me) → authenticated | unauthenticated
 *   unauthenticated → magic link / OAuth (server-side redirect) → authenticated
 *   authenticated → signOut() → unauthenticated
 *
 * Passwordless: the sign-in screen requests an emailed one-time link (or
 * bounces through OAuth), so there is no client-side credentials call —
 * both flows land back with a session cookie and refresh() picks it up.
 *
 * Free-mode never instantiates this provider — its components don't
 * read auth state. The only consumers are the cloud sign-in screen
 * and the cloud landing/editor wrapper.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { authApi, type MeResponse } from './api.js';

export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: MeResponse['user'] | null;
  organizations: MeResponse['organizations'];
  /** Slug of the active org. Picked from `activeOrgId` if set, else
   * the first org in the list. Null if the user has no orgs. */
  activeOrgSlug: string | null;
  signOut(): Promise<void>;
  /** Switch the session's active org. Fetches /me afterwards so the
   * `activeOrgSlug` reflects the change. */
  switchOrg(slug: string): Promise<void>;
  /** Create an org (the caller becomes its owner), switch the session
   * into it, and re-fetch /me so `activeOrgSlug` points at the new org. */
  createOrg(name: string, slug?: string): Promise<void>;
  /** Re-fetch /me. Useful after creating an org or being added to one. */
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Override the API base URL. Tests inject a value pointing at the local stack. */
  apiBaseUrl?: string;
}

export function AuthProvider({ children, apiBaseUrl }: AuthProviderProps): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);

  // Memoize the api client by baseUrl so the auth callbacks below have a
  // stable identity across renders unless the base URL itself changes.
  const api = useMemo(
    () => authApi(apiBaseUrl !== undefined ? { baseUrl: apiBaseUrl } : {}),
    [apiBaseUrl],
  );

  const refresh = useCallback(async (): Promise<void> => {
    const result = await api.me();
    if (result) {
      setMe(result);
      setStatus('authenticated');
    } else {
      setMe(null);
      setStatus('unauthenticated');
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async (): Promise<void> => {
    await api.signOut();
    setMe(null);
    setStatus('unauthenticated');
  }, [api]);

  const switchOrg = useCallback(
    async (slug: string): Promise<void> => {
      await api.switchOrg(slug);
      await refresh();
    },
    [api, refresh],
  );

  const createOrg = useCallback(
    async (name: string, slug?: string): Promise<void> => {
      const org = await api.createOrg(name, slug);
      // Activate the new org so the shell lands the user in it (otherwise
      // the session's active org is unchanged and they'd stay where they
      // were), then refresh /me to reflect the switch.
      await api.switchOrg(org.slug);
      await refresh();
    },
    [api, refresh],
  );

  const activeOrgSlug = (() => {
    if (!me) return null;
    if (me.activeOrgId) {
      const found = me.organizations.find((o) => o.id === me.activeOrgId);
      if (found) return found.slug;
    }
    return me.organizations[0]?.slug ?? null;
  })();

  const value: AuthContextValue = {
    status,
    user: me?.user ?? null,
    organizations: me?.organizations ?? [],
    activeOrgSlug,
    signOut,
    switchOrg,
    createOrg,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
